import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updatePassword,
  GoogleAuthProvider,
  signInWithPopup,
  updateProfile,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { auth, db } from '../../firebase';
import { doc, getDoc } from 'firebase/firestore';

export type SubscriptionValidationResponse = {
  status: 'ok' | 'error';
  subscription: {
    startTimeMillis: number;
    expiryTimeMillis: number;
    orderId: string;
    acknowledgementState: number;
    autoRenewing: boolean;
    countryCode: string;
    paymentState: number;
    priceAmountMicros: number;
    priceCurrencyCode: string;
    status: number;
    service: string;
    cancelReason: 0 | 1 | 2 | 3;
  };
};

export const signInUsingEmail = async (email: string, password: string) => {
  try {
    if (!email || !password) {
      throw Error('auth/no-email-password');
    }
    const user = await signInWithEmailAndPassword(auth, email, password);
    return user;
  } catch (e) {
    console.log(e);
    return null;
  }
};

export const signUpNewUser = async (
  email: string,
  password?: string,
  name?: string
) => {
  let randomPassword = '';
  if (!password) {
    const chars =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const randomArray = new Uint8Array(20);
    crypto.getRandomValues(randomArray);
    randomArray.forEach((number) => {
      randomPassword += chars[number % chars.length];
    });
  }

  try {
    const signUp = await signUpUsingEmail(
      email,
      password ? password : randomPassword,
      name
    );
    return signUp;
  } catch (e) {
    console.log(e);
    return null;
  }
};

export const signUpUsingEmail = async (
  email: string,
  password: string,
  name?: string
) => {
  try {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    if (auth.currentUser)
      updateProfile(auth.currentUser, { displayName: name });
    return result;
  } catch (error) {
    console.log(error);
    return undefined;
  }
};

export const changePassword = async (newPassword: string) => {
  const user = auth.currentUser;
  try {
    if (user) {
      await updatePassword(user, newPassword);
    }
    return user;
  } catch (e) {
    console.log(e);
    return null;
  }
};

export const signUpWithGoogle = async () => {
  const provider = new GoogleAuthProvider();
  const signIn = await signInWithPopup(auth, provider);
  if (signIn) {
    const credential = GoogleAuthProvider.credentialFromResult(signIn);
    const user = signIn.user;
    if (credential) {
      return { user };
    } else {
      return { user: null as any, error: 'Unexpected error ocurred' };
    }
  }
};

export const validateForPurchase = async () => {
  if (auth.currentUser?.email) {
    const docRef = doc(db, 'users', auth.currentUser?.email);
    const docSnap = await getDoc(docRef);
    const data = docSnap.data();
    if (data) {
      if (data.expiryTimeMillis < Date.now()) {
        return { status: true, message: 'old user' };
      } else {
        return {
          status: false,
          message:
            'You already have an active subscription. For more details go to dashboard',
        };
      }
    } else {
      return { status: true, message: 'new user' };
    }
  }
  return {
    status: false,
    message: 'You must signIn to subscribe.',
  };
};

export const signOutUser = async () => {
  try {
    await auth.signOut();

    return true;
  } catch (_e) {
    return false;
  }
};

export const passwordReset = async (email: string) => {
  if (email !== '') {
    try {
      await sendPasswordResetEmail(auth, email);
      return true;
    } catch (error) {
      console.log(error);
      return false;
    }
  } else {
    return false;
  }
};

export const changeUserName = async (newName: string) => {
  try {
    if (auth.currentUser) {
      await updateProfile(auth.currentUser, { displayName: newName });
      return true;
    }
  } catch {
    return false;
  }
};
